import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type {
  PartyEligibilityReason,
  PartyEligibilityResponse,
  PartyEligibilityState,
  PartyMemberEligibility,
  PartyMemberEligibilityState,
  PartyMemberRemediation,
  PartyPlacementAction,
} from "@bw/shared/types/generated";
import { Avatar } from "../../../common/components/Avatar";
import {
  PARTY_MEMBER_REASON_LABELS,
  PARTY_MEMBER_STATE_LABELS,
  safePartyMemberName,
} from "../lib/partyMemberPresentation";

const PARTY_STATE_LABELS: Record<PartyEligibilityState, string> = {
  ready: "Ready",
  blocked: "Blocked",
  waiting: "Waiting",
  on_court: "On court",
  unavailable: "Unavailable",
};

const PARTY_REASON_COPY: Record<PartyEligibilityReason, string> = {
  eligible: "Everyone is ready to play.",
  party_not_open: "This party is not open for a new placement.",
  queue_closed: "Court placement is paused at this facility.",
  member_needs_action: "One or more members need action before placement.",
  party_too_large: "This party does not fit an enabled court.",
  facility_closing: "There is not enough drop-in time left for this party.",
  no_enabled_court: "No enabled court can take this party right now.",
  eligibility_unavailable:
    "Party eligibility is unavailable. Retry before placing the party.",
};

const REMEDIATION_LABELS: Record<PartyMemberRemediation, string> = {
  none: "No action needed",
  check_in: "Check in",
  present_pass_to_staff: "Show check-in pass",
  renew_session: "Renew session",
  view_current_activity: "View current activity",
  retry_status: "Retry status",
};

const MODE_LABELS: Record<"auto" | "manual" | "self_serve", string> = {
  auto: "Auto-assign",
  manual: "Staff-run",
  self_serve: "Self-serve",
};

const ACTION_LABELS: Record<PartyPlacementAction, string | null> = {
  choose_court: "Choose a court",
  join_auto_queue: "Join auto queue",
  join_staff_queue: "Join staff queue",
  view_queue: "View queue",
  view_court: "View court",
  none: null,
};

export interface EligibilityFocusRequest {
  key: number;
  target: "member" | "action" | "summary" | "recovery" | "court";
  memberId?: string;
  courtId?: string;
}

interface PartyEligibilityPanelProps {
  eligibility: PartyEligibilityResponse;
  canPlaceParty: boolean;
  canRemoveMembers: boolean;
  canViewActivity: boolean;
  actionsDisabled?: boolean;
  actionPending?: boolean;
  removePending?: boolean;
  showPartyAction?: boolean;
  activityHref?: string;
  partyHref?: string;
  partyHrefLabel?: string;
  title?: string;
  headingLevel?: "h2" | "h3";
  focusRequest?: EligibilityFocusRequest | null;
  onPlaceParty?: () => void;
  onRemoveMember?: (memberId: string) => void;
}

function safeDropInHref(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  try {
    const parsed = new URL(value, "https://player.badmintonwarriors.local");
    if (
      parsed.origin !== "https://player.badmintonwarriors.local" ||
      (parsed.pathname !== "/dropin" && !parsed.pathname.startsWith("/dropin/"))
    ) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function memberStateColor(
  state: PartyMemberEligibilityState,
): "success" | "primary" | "warning" {
  if (state === "eligible") return "success";
  if (state === "waiting" || state === "on_court") return "primary";
  return "warning";
}

function partyStateColor(
  state: PartyEligibilityState,
): "success" | "primary" | "warning" {
  if (state === "ready") return "success";
  if (state === "waiting" || state === "on_court") return "primary";
  return "warning";
}

function blockerSummary(eligibility: PartyEligibilityResponse): string {
  if (eligibility.reason !== "member_needs_action") {
    return PARTY_REASON_COPY[eligibility.reason];
  }

  const blockedNames = (eligibility.members ?? [])
    .map((member, index) => ({ member, index }))
    .filter(
      ({ member }) =>
        member.state === "needs_action" || member.state === "unavailable",
    )
    .map(({ member, index }) => safePartyMemberName(member, index));
  if (blockedNames.length === 0) {
    return PARTY_REASON_COPY.member_needs_action;
  }
  if (blockedNames.length === 1) {
    return `${blockedNames[0]} needs action before the party can be placed.`;
  }
  if (blockedNames.length === 2) {
    return `${blockedNames[0]} and ${blockedNames[1]} need action before placement.`;
  }
  return `${blockedNames[0]}, ${blockedNames[1]}, and ${blockedNames.length - 2} others need action before placement.`;
}

function placementActionHref(
  action: PartyPlacementAction,
  eligibility: PartyEligibilityResponse,
  activityHref: string | undefined,
): string | null {
  if (action === "choose_court") {
    const params = new URLSearchParams({ partyId: eligibility.party_id });
    return `/dropin/${eligibility.facility_id}/court-board?${params.toString()}`;
  }
  if (action === "view_queue" || action === "view_court") {
    return safeDropInHref(activityHref);
  }
  return null;
}

function memberEligibilityChanged(
  previous: PartyMemberEligibility,
  next: PartyMemberEligibility,
): boolean {
  return (
    previous.display_name !== next.display_name ||
    previous.avatar_url !== next.avatar_url ||
    previous.is_current_user !== next.is_current_user ||
    previous.is_leader !== next.is_leader ||
    previous.admission_state !== next.admission_state ||
    previous.activity_state !== next.activity_state ||
    previous.state !== next.state ||
    previous.reason !== next.reason ||
    previous.remediation !== next.remediation ||
    previous.action_url !== next.action_url ||
    previous.session_expires_at !== next.session_expires_at
  );
}

export function hasPartyEligibilityDisplayChange(
  previous: PartyEligibilityResponse | null | undefined,
  next: PartyEligibilityResponse | null | undefined,
): boolean {
  if (!previous || !next) return previous !== next;
  if (
    previous.party_id !== next.party_id ||
    previous.facility_id !== next.facility_id ||
    previous.facility_name !== next.facility_name ||
    previous.assignment_mode !== next.assignment_mode ||
    previous.state !== next.state ||
    previous.reason !== next.reason ||
    previous.leader_can_place !== next.leader_can_place ||
    previous.action !== next.action ||
    previous.minimum_play_end !== next.minimum_play_end
  ) {
    return true;
  }

  const previousMembers = previous.members ?? [];
  const nextMembers = next.members ?? [];
  if (previousMembers.length !== nextMembers.length) return true;
  const previousById = new Map(
    previousMembers.map((member) => [member.member_id, member]),
  );
  return nextMembers.some((member) => {
    const prior = previousById.get(member.member_id);
    return !prior || memberEligibilityChanged(prior, member);
  });
}

export function createEligibilityFocusRequest(
  key: number,
  previous: PartyEligibilityResponse | null | undefined,
  next: PartyEligibilityResponse | null | undefined,
): EligibilityFocusRequest {
  if (!next) return { key, target: "recovery" };
  if (
    !previous ||
    previous.party_id !== next.party_id ||
    previous.facility_id !== next.facility_id
  ) {
    return { key, target: "summary" };
  }

  const previousById = new Map(
    (previous.members ?? []).map((member) => [member.member_id, member]),
  );
  const changedMember = (next.members ?? []).find((member) => {
    const prior = previousById.get(member.member_id);
    return !prior || memberEligibilityChanged(prior, member);
  });
  if (changedMember) {
    return {
      key,
      target: "member",
      memberId: changedMember.member_id,
    };
  }

  const nextMemberIds = new Set(
    (next.members ?? []).map((member) => member.member_id),
  );
  if (
    (previous.members ?? []).some(
      (member) => !nextMemberIds.has(member.member_id),
    ) ||
    previous.state !== next.state ||
    previous.reason !== next.reason ||
    previous.minimum_play_end !== next.minimum_play_end ||
    previous.facility_name !== next.facility_name
  ) {
    return { key, target: "summary" };
  }

  if (
    previous.action !== next.action ||
    previous.leader_can_place !== next.leader_can_place ||
    previous.assignment_mode !== next.assignment_mode
  ) {
    return {
      key,
      target: next.action === "none" ? "summary" : "action",
    };
  }

  return { key, target: "summary" };
}

export function PartyEligibilityPanel({
  eligibility,
  canPlaceParty,
  canRemoveMembers,
  canViewActivity,
  actionsDisabled = false,
  actionPending = false,
  removePending = false,
  showPartyAction = true,
  activityHref,
  partyHref,
  partyHrefLabel = "View party",
  title = "Party readiness",
  headingLevel = "h2",
  focusRequest,
  onPlaceParty,
  onRemoveMember,
}: PartyEligibilityPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const actionRef = useRef<HTMLElement>(null);
  const actionRegionRef = useRef<HTMLElement>(null);
  const memberRefs = useRef(new Map<string, HTMLElement>());
  const setActionRef = (element: HTMLElement | null) => {
    actionRef.current = element;
  };
  const members = eligibility.members ?? [];
  const actionLabel = ACTION_LABELS[eligibility.action];
  const actionHref = placementActionHref(
    eligibility.action,
    eligibility,
    activityHref,
  );
  const actionPlacesParty =
    eligibility.action === "choose_court" ||
    eligibility.action === "join_auto_queue" ||
    eligibility.action === "join_staff_queue";
  const canShowAction =
    showPartyAction &&
    actionLabel !== null &&
    (actionPlacesParty ? canPlaceParty : canViewActivity);
  const actionDisabled =
    actionsDisabled ||
    actionPending ||
    (actionPlacesParty && !eligibility.leader_can_place) ||
    ((eligibility.action === "join_auto_queue" ||
      eligibility.action === "join_staff_queue") &&
      !onPlaceParty) ||
    ((eligibility.action === "choose_court" ||
      eligibility.action === "view_queue" ||
      eligibility.action === "view_court") &&
      !actionHref);

  useEffect(() => {
    if (
      !focusRequest ||
      focusRequest.target === "recovery" ||
      focusRequest.target === "court"
    )
      return;

    let element: HTMLElement | null | undefined;
    if (focusRequest.target === "member" && focusRequest.memberId) {
      element = memberRefs.current.get(focusRequest.memberId);
    } else if (focusRequest.target === "action") {
      element = actionRef.current ?? actionRegionRef.current;
    } else {
      element = summaryRef.current;
    }
    element ??= summaryRef.current ?? panelRef.current;
    element?.focus();
    if (
      focusRequest.target === "action" &&
      element === actionRef.current &&
      document.activeElement !== element
    ) {
      element = actionRegionRef.current ?? summaryRef.current;
      element?.focus();
    }
    element?.scrollIntoView?.({ block: "nearest" });
  }, [focusRequest]);

  return (
    <Paper
      component="section"
      ref={panelRef}
      variant="outlined"
      role="region"
      aria-label="Party eligibility"
      tabIndex={-1}
      sx={{ p: 2 }}
    >
      <Stack spacing={2}>
        <Stack
          component="header"
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "flex-start" }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component={headingLevel} variant="h6">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="strong" sx={{ color: "text.primary" }}>
                {eligibility.facility_name?.trim() || "Facility unavailable"}
              </Box>
              <Box component="span" aria-hidden="true">
                {" "}
                ·{" "}
              </Box>
              <Box component="span">
                {eligibility.assignment_mode
                  ? MODE_LABELS[eligibility.assignment_mode]
                  : "Mode unavailable"}
              </Box>
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={partyStateColor(eligibility.state)}
            label={PARTY_STATE_LABELS[eligibility.state]}
          />
        </Stack>

        <Typography
          ref={summaryRef}
          role="status"
          tabIndex={-1}
          sx={{ fontWeight: 700 }}
        >
          {blockerSummary(eligibility)}
        </Typography>

        <Stack spacing={1} data-member-count={members.length}>
          {members.map((member, index) => {
            const name = safePartyMemberName(member, index);
            const isCurrentUser = member.is_current_user;
            const remediationHref = isCurrentUser
              ? safeDropInHref(member.action_url)
              : null;
            const isBlocked =
              member.state === "needs_action" || member.state === "unavailable";
            const canRemove =
              canRemoveMembers &&
              !isCurrentUser &&
              isBlocked &&
              onRemoveMember !== undefined;

            return (
              <Paper
                component="article"
                key={member.member_id}
                ref={(element) => {
                  if (element) {
                    memberRefs.current.set(member.member_id, element);
                  } else {
                    memberRefs.current.delete(member.member_id);
                  }
                }}
                variant="outlined"
                role="group"
                aria-label={name}
                tabIndex={-1}
                sx={{ p: 1.5 }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Avatar
                      name={name}
                      avatarUrl={member.avatar_url}
                      size={32}
                    />
                    <Typography sx={{ minWidth: 0, flex: 1, fontWeight: 700 }}>
                      {name}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={memberStateColor(member.state)}
                      label={PARTY_MEMBER_STATE_LABELS[member.state]}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {PARTY_MEMBER_REASON_LABELS[member.reason]}
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignItems: { sm: "center" } }}
                  >
                    {remediationHref && member.remediation !== "none" ? (
                      <Button
                        component={Link}
                        to={remediationHref}
                        variant="outlined"
                        size="small"
                      >
                        {REMEDIATION_LABELS[member.remediation]}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {member.remediation === "none"
                          ? REMEDIATION_LABELS.none
                          : `Next: ${REMEDIATION_LABELS[member.remediation]}`}
                      </Typography>
                    )}
                    {canRemove ? (
                      <Button
                        type="button"
                        color="error"
                        variant="outlined"
                        size="small"
                        aria-label={`Remove ${name}`}
                        onClick={() => onRemoveMember(member.member_id)}
                        disabled={actionsDisabled || removePending}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        {canShowAction || partyHref ? (
          <Stack
            component="footer"
            ref={actionRegionRef}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            aria-label="Party actions"
            tabIndex={-1}
          >
            {canShowAction ? (
              actionDisabled ? (
                <Button
                  ref={setActionRef}
                  type="button"
                  variant="contained"
                  size="small"
                  disabled
                >
                  {actionLabel}
                </Button>
              ) : actionHref ? (
                <Button
                  component={Link}
                  ref={setActionRef}
                  to={actionHref}
                  variant="contained"
                  size="small"
                >
                  {actionLabel}
                </Button>
              ) : (
                <Button
                  ref={setActionRef}
                  type="button"
                  variant="contained"
                  size="small"
                  onClick={onPlaceParty}
                >
                  {actionLabel}
                </Button>
              )
            ) : null}
            {partyHref ? (
              <Button
                component={Link}
                to={partyHref}
                variant="outlined"
                size="small"
              >
                {partyHrefLabel}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}
